// Canonical permission IDs (each equates to a page / menu item). These MUST match the permission `id`
// values seeded in the backend (AccountManagement Permission table). One permission can cover several
// pages (e.g. `feedback` covers all feedback pages; `users` covers both the Users and Roles tabs).
export const Perm = {
    // Covers the whole of Admin except Skinning and Deleted Locations: Configuration (organizations, brands,
    // locations and everything beneath them), the Content String Catalogue, Advertisers and Oracle
    // Certification. Those four are one job — looking after TouchTab's own reference data — so they are
    // granted together rather than four ways (TTD-159).
    adminConfiguration: 'admin-configuration',
    // Restoring or permanently destroying a deleted location is a materially different job from the rest of
    // the Admin section, so it is grantable on its own rather than riding on super-admin membership.
    adminDeletedLocations: 'admin-deleted-locations',
    adminSkinning: 'admin-skinning',
    configurationContentStrings: 'configuration-content-strings',
    configurationDayParts: 'configuration-day-parts',
    // The nav item for this is commented out pending the CheckPlease tempdb going away, but the page is real
    // and does full CRUD, so the route needs a gate whether or not anything currently links to it.
    configurationLocationGroups: 'configuration-location-groups',
    configurationOnboarding: 'configuration-onboarding',
    configurationRevenueCenters: 'configuration-revenue-centers',
    configurationTips: 'configuration-tips',
    devices: 'devices',
    feedback: 'feedback',
    location: 'location',
    paymentsTransactions: 'payments-transactions',
    // The Media Network section is granted per nav item rather than as a whole: selling banner space, building
    // cross-sell offers, producing artwork and reading delivery reports are different jobs, and a brand will
    // want to hand them to different people. Each of the six below gates exactly one nav item.
    //
    // Was promotions-* until the service became a Media Network. Renamed while nothing had been granted —
    // once rows exist, changing a permission id is a data migration rather than one file.
    mediaNetworkAdBanner: 'media-network-ad-banner',
    mediaNetworkCrossSell: 'media-network-cross-sell',
    // The shared creative library. Its own permission because uploading and managing artwork is a distinct
    // job from scheduling it — often a different person entirely — and both features draw on the same shelf.
    mediaNetworkMediaLibrary: 'media-network-media-library',
    // Attribution reference data. Separate from the library because these are ad-sales setup: who bought the
    // space and how it is categorised, not the artwork itself.
    mediaNetworkAdvertisers: 'media-network-advertisers',
    mediaNetworkTags: 'media-network-tags',
    mediaNetworkReports: 'media-network-reports',
    users: 'users',
} as const;

export type PermissionId = (typeof Perm)[keyof typeof Perm];

// Permissions for TouchTab's own admin surfaces. They only mean anything inside the TouchTab organization,
// so offering them when building a customer's role is noise at best and misleading at worst — it implies a
// customer role could be granted access to screens that are not theirs. Hidden from the role editor for
// everyone else; see Permissions.tsx, which hides them from view without dropping them from the role.
export const ADMIN_ONLY_PERMISSIONS: readonly string[] = [
    Perm.adminConfiguration,
    Perm.adminDeletedLocations,
    Perm.adminSkinning,
];
